import { authFetchGET } from '../utils/auth-fetch';
import { VesselConfigData } from '../models/vessel-performance-model';

const env = (<any>window)["appenv"].services.performanceapi;

export interface PerformanceData
{
    stw: (number|null)[]|null;
    sog: (number|null)[]|null;
    mepower: (number|null)[]|null;
    merpm: (number|null)[]|null;
    ts: number[];
    imoNo: number;
    length: number;
}

export class PerformanceService {
    static async fetchVesselConfigurationData(imoNo: number): Promise<VesselConfigData|null> {
        try {
            const data = await authFetchGET(env.scope,
                `${env.baseurl}/api/Vessel/${imoNo}/Configuration`);

            if (!data) {
                console.warn('No vessel configuration data for IMO: ', imoNo);
                return null;
            }

            let maxRpm = data.mainEng1.maxShaftRpm;
            let maxPower = data.mainEng1.mcr;

            if (data.hasTwoMainEngines) {
                maxRpm = (data.mainEng1.maxShaftRpm + data.mainEng2.maxShaftRpm) / 2;
                maxPower = data.mainEng1.mcr + data.mainEng2.mcr;
            }

            // Main engine(s) MCR is in megawatts, convert to kilowatts here
            maxPower = maxPower*1000;

            return {
                maxPower,
                maxRpm,
                maxSpeed: data.maxSpeed
            };
        } catch (error) {
            console.warn(error);
            return null;
        }
    }

    static async fetchPerformanceData(imoNo: number, fromDate: Date, toDate: Date): Promise<PerformanceData|null> {

        //   9: Speed Through Water (Longitudinal)
        //  10: Speed Over Ground (GPS)
        //  14: Speed Through Water (Transverse)
        //  60: ME 1 Power
        //  61: ME 1 Shaft RPM
        // 800: ME 2 Power
        // 801: ME 2 Shaft RPM

        // NOTE: Madrid & Triple E classes are the only ones (known) to have dual engine
        // https://stst.maerskstarconnect.com/PerformanceAPI/swagger/ui/index#!/VesselConfiguration/VesselConfiguration_Get knows if it has dual engine

        const query = {
            'period.from': fromDate.toISOString(),
            'period.to': toDate.toISOString(),
            'dataPointIds': '9,10,60,61,800,801'
        };

        try {
            const data = await authFetchGET(env.scope,
                `${env.baseurl}/api/Vessel/${imoNo}/Payload/CAMS`, query);

            if (!data.hasData) {
                console.warn('No PAPI data for IMO: ', imoNo);
                return null;
            }

            const stw = data.dataPoints.find((i: any) => i.id === 9)?.series || null;
            const sog = data.dataPoints.find((i: any) => i.id === 10)?.series || null;
            const mepower = data.dataPoints.find((i: any) => i.id === 60)?.series || null;
            const merpm = data.dataPoints.find((i: any) => i.id === 61)?.series || null;

            try {
                const me2power = data.dataPoints.find((i: any) => i.id === 800).series;
                const me2rpm = data.dataPoints.find((i: any) => i.id === 801).series;

                if (me2power.length === mepower.length) {
                    for (let i=0;i<mepower.length;i++) {
                        mepower[i] += me2power[i];
                    }
                }

                if (me2rpm.length === merpm.length) {
                    for (let i=0;i<merpm.length;i++) {
                        merpm[i] = (merpm[i]+me2rpm[i])*0.5;
                    }
                }

            } catch (error) {}
            const ts = data.timestamps;

            return {
                stw,
                sog,
                mepower,
                merpm,
                ts,
                imoNo: imoNo,
                length: sog.length
            };

        } catch (error) {
            console.warn(error);
            return null;
        }
    }

    static async fetchSystemImoNos() {
        try {
            const data: number[] = await authFetchGET(env.scope,
                `${env.baseurl}/api/System/ImoNos`);

            if (!data) {
                return [];
            }

            return data;
        } catch (error) {
            console.warn(error);
        }
    }

}
